import React, { useRef, useCallback, useId, useLayoutEffect } from 'react';
import { Paperclip, XCircle, Loader2, Send, Mic, Video, Square } from 'lucide-react';
import AttachmentPreview from './AttachmentPreview';
import type { Theme, Attachment } from '../../types';
import {
  ALLOWED_FILE_TYPES,
  MAX_INPUT_LENGTH,
  INPUT_WARNING_THRESHOLD,
} from '../../constants/fileUpload';
import { resolveThemeToken, isColorToken } from '../../utils/theme';
import type { WidgetStrings } from '../../utils/i18n';
import { DEFAULT_WIDGET_STRINGS, formatWidgetString } from '../../utils/i18n';

interface InputAreaProps {
  input: string;
  setInput: (value: string) => void;
  handleSend: () => void;
  /** True while a send is in flight; with `onStop` the send button becomes a Stop button. */
  loading: boolean;
  /** Cancels the in-flight request. When omitted, loading shows a disabled spinner. */
  onStop?: () => void;
  attachments: Attachment[];
  attachmentErrors: string[];
  allowAttachments: boolean;
  allowVoice?: boolean;
  allowVideo?: boolean;
  theme: Theme;
  fileInputRef: React.RefObject<HTMLInputElement | null>;
  textAreaRef: React.RefObject<HTMLTextAreaElement | null>;
  onFilesSelected: (files: FileList) => void;
  onRemoveAttachment: (id: string) => void;
  placeholder: string;
  onVoiceClick?: () => void;
  onVideoClick?: () => void;
  disabled?: boolean;
  maxInputLength?: number;
  inputWarningThreshold?: number;
  allowedFileTypes?: string[];
  strings?: WidgetStrings;
}

const InputArea = React.memo(function InputArea({
  input,
  setInput,
  handleSend,
  loading,
  onStop,
  attachments,
  attachmentErrors,
  allowAttachments,
  allowVoice,
  allowVideo,
  theme,
  fileInputRef,
  textAreaRef,
  onFilesSelected,
  onRemoveAttachment,
  placeholder,
  onVoiceClick,
  onVideoClick,
  disabled = false,
  maxInputLength,
  inputWarningThreshold,
  allowedFileTypes,
  strings = DEFAULT_WIDGET_STRINGS,
}: InputAreaProps) {
  const fallbackRef = useRef<HTMLTextAreaElement>(null);
  const taRef = textAreaRef || fallbackRef;
  const inputId = useId();
  const helperId = `${inputId}-helper`;
  const errorId = `${inputId}-errors`;
  const accept =
    allowedFileTypes && allowedFileTypes.length > 0
      ? allowedFileTypes.join(',')
      : Array.from(ALLOWED_FILE_TYPES).join(',');
  const inputLimit =
    typeof maxInputLength === 'number' && maxInputLength > 0 ? maxInputLength : MAX_INPUT_LENGTH;
  const warningThresholdRaw =
    typeof inputWarningThreshold === 'number' ? inputWarningThreshold : INPUT_WARNING_THRESHOLD;
  const warningThreshold = Math.max(0, Math.min(1, warningThresholdRaw));
  const primary = resolveThemeToken(theme.primary, 'backgroundColor');
  const footerBackground = resolveThemeToken(theme.header || 'bg-white', 'backgroundColor');
  const textColor = resolveThemeToken(theme.text, 'color');
  const hoverClass = isColorToken(theme.hover) ? '' : theme.hover;
  const minTextareaHeight = 42;
  const maxTextareaHeight = 160;

  const resizeTextArea = useCallback(
    (el: HTMLTextAreaElement | null) => {
      if (!el) return;
      el.style.height = `${minTextareaHeight}px`;
      // An empty draft already fits the minimum box. Reading scrollHeight here forces
      // layout of the entire newly mounted panel, including during its idle warm-up.
      if (!el.value) return;
      const nextHeight = Math.min(el.scrollHeight, maxTextareaHeight);
      el.style.height = `${Math.max(minTextareaHeight, nextHeight)}px`;
    },
    [minTextareaHeight, maxTextareaHeight]
  );

  useLayoutEffect(() => {
    resizeTextArea(taRef.current);
  }, [input, resizeTextArea, taRef]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      // Safari can clear isComposing before the key that confirms a candidate.
      if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return;
      if (disabled) return;
      if (e.key === 'Enter' && !e.shiftKey) {
        if (loading) return;
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend, loading, disabled]
  );

  const onChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value.slice(0, inputLimit);
      setInput(value);
    },
    [setInput, inputLimit]
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) onFilesSelected(e.target.files);
      e.target.value = '';
    },
    [onFilesSelected]
  );

  const handleAttachClick = useCallback(() => {
    fileInputRef.current?.click();
  }, [fileInputRef]);

  const showCounter = input.length >= inputLimit * warningThreshold;
  const showStop = loading && typeof onStop === 'function';
  const sendDisabled =
    disabled || loading || (!input.trim() && (!attachments || attachments.length === 0));

  const describedBy = [helperId, attachmentErrors?.length ? errorId : null]
    .filter(Boolean)
    .join(' ');

  return (
    <div
      className={`rw-input-area border-t px-4 py-3 ${footerBackground.className}`}
      style={footerBackground.style}
    >
      {attachmentErrors?.length > 0 && (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Enable native keyboard scrolling in the bounded alert.
        <div id={errorId} role="alert" tabIndex={0} className="rw-attachment-errors">
          {attachmentErrors.map((error, idx) => (
            <div key={`${error}-${idx}`}>{error}</div>
          ))}
        </div>
      )}
      {attachments?.length > 0 && (
        // eslint-disable-next-line jsx-a11y/no-redundant-roles -- Preserve list semantics when list-style is none.
        <ul role="list" className="rw-attachments" aria-label={strings.selectedAttachments}>
          {attachments.map(att => (
            <li key={att.id} className="rw-selected-attachment">
              <AttachmentPreview attachment={att} />
              <button
                type="button"
                aria-label={formatWidgetString(strings.removeAttachment, { name: att.name })}
                onClick={event => {
                  const focused = event.currentTarget === document.activeElement;
                  const item = event.currentTarget.parentElement!;
                  const sibling = item.nextElementSibling || item.previousElementSibling;
                  onRemoveAttachment(att.id);
                  if (focused) {
                    const next = sibling
                      ? sibling.querySelector<HTMLButtonElement>('button')
                      : taRef.current;
                    const target =
                      next && !next.disabled
                        ? next
                        : item
                            .closest('.rw-widget')
                            ?.querySelector<HTMLElement>('button:not([disabled])');
                    target?.focus();
                  }
                }}
                className="rw-attachment-remove rounded focus:outline-2 focus:outline-blue-500"
              >
                <XCircle className="w-4 h-4" aria-hidden="true" />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div
        className={`rw-composer-row flex items-center gap-2${allowVoice || allowVideo ? ' rw-composer-expanded' : ''}`}
      >
        {allowAttachments && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              hidden
              accept={accept}
              onChange={handleFileInputChange}
              disabled={disabled}
            />
            <button
              onClick={handleAttachClick}
              type="button"
              disabled={disabled}
              className="p-2 rounded hover:bg-gray-100 focus:outline-2 focus:outline-blue-500"
              aria-label={strings.attachFiles}
            >
              <Paperclip className="w-4 h-4" aria-hidden="true" />
            </button>
          </>
        )}
        {allowVoice && (
          <button
            aria-label={strings.startVoiceInput}
            onClick={onVoiceClick}
            type="button"
            disabled={disabled}
            className="p-2 rounded hover:bg-gray-100 focus:outline-2 focus:outline-blue-500"
          >
            <Mic className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
        {allowVideo && (
          <button
            aria-label={strings.startVideoChat}
            onClick={onVideoClick}
            type="button"
            disabled={disabled}
            className="p-2 rounded hover:bg-gray-100 focus:outline-2 focus:outline-blue-500"
          >
            <Video className="w-4 h-4" aria-hidden="true" />
          </button>
        )}
        <div className="rw-composer-field flex-1 relative">
          <textarea
            ref={taRef}
            value={input}
            rows={1}
            enterKeyHint="send"
            onChange={onChange}
            onKeyDown={onKeyDown}
            placeholder={placeholder || strings.messagePlaceholder}
            maxLength={inputLimit}
            id={inputId}
            aria-describedby={describedBy}
            className="w-full resize-none rounded-xl border px-3 py-2 text-sm bg-white focus:outline-2 focus:outline-blue-500"
            style={{ height: `${minTextareaHeight}px` }}
            aria-label={strings.messageInputAria}
            disabled={disabled}
          />
          <span id={helperId} className="sr-only">
            {strings.messageInputHelp}
          </span>
          {showCounter && (
            <span
              className="absolute bottom-1 text-[11px] text-gray-600"
              style={{ insetInlineEnd: '0.5rem' }}
            >
              {input.length}/{inputLimit}
            </span>
          )}
        </div>
        <button
          onClick={showStop ? onStop : handleSend}
          disabled={!showStop && sendDisabled}
          aria-label={showStop ? strings.stopGenerating : strings.sendMessage}
          title={showStop ? strings.stopGenerating : undefined}
          type="button"
          className={`rw-send-button rounded-full p-2 ${primary.className} ${textColor.className} ${hoverClass} disabled:opacity-50 shadow focus:outline-2 focus:outline-blue-500`}
          style={{ ...primary.style, ...textColor.style }}
        >
          {showStop ? (
            <Square className="w-4 h-4" fill="currentColor" aria-hidden="true" />
          ) : loading ? (
            <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
          ) : (
            <Send className="w-4 h-4" aria-hidden="true" />
          )}
        </button>
      </div>
    </div>
  );
});

export default InputArea;
